/**
 * SCP Product Card
 * Handles image carousel, color swatches, and quick-add functionality
 */

// Diagnostic logging shared with assets/scp-feature-carousel.js. Both files
// emit "[SCP ATC]" lines so a single console filter surfaces the full ATC
// flow regardless of which file owns the handler. Gated behind
// `window.SCP_ATC_DEBUG === true` (auto-enabled by ?scp_debug=1 in the
// carousel file) so production stays silent.
const _scpDebugLog = (...args) => {
  if (typeof window !== "undefined" && window.SCP_ATC_DEBUG === true) {
    //console.log("[SCP ATC]", ...args);
  }
};

class SCPProductCard {
  constructor(element) {
    this.card = element;
    this.productId = this.card.dataset.productId;
    this.productHandle = this.card.dataset.productHandle;

    // Pending timeouts for cleanup
    this._pendingTimeouts = [];

    // Get product data safely
    this.productData = this.getProductData();

    // Carousel elements
    this.carousel = this.card.querySelector("[data-scp-carousel]");
    this.carouselTrack = this.card.querySelector("[data-carousel-track]");
    this.slides = this.card.querySelectorAll("[data-carousel-slide]");
    this.prevButton = this.card.querySelector("[data-carousel-prev]");
    this.nextButton = this.card.querySelector("[data-carousel-next]");

    // Current slide index
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;

    // Quick add elements (there may be more than one trigger per card -
    // e.g. a media-anchored hover button AND a price-row inline mobile button).
    this.quickAddTriggers = this.card.querySelectorAll(
      "[data-quick-add-trigger]",
    );
    this.variantOverlay = this.card.querySelector("[data-variant-overlay]");
    this.overlayClose = this.card.querySelector("[data-overlay-close]");
    this.sizeOptions = this.card.querySelectorAll("[data-size-option]");

    // Price wrapper
    this.priceWrapper = this.card.querySelector("[data-price-wrapper]");

    // Mobile detection
    this.isMobile = window.innerWidth <= 989;

    // Touch handling
    this.touchStartX = 0;
    this.touchEndX = 0;

    // Hover handling
    this.hoverTimeout = null;
    this.isHoverActive = false;

    this.init();
  }

  init() {
    this.initCarousel();
    this.initQuickAdd();
    this.initMobileBehavior();
    this.initColorSwatches();

    // Update on resize
    window.addEventListener(
      "resize",
      this.debounce(() => {
        this.isMobile = window.innerWidth <= 989;
      }, 250),
    );
  }

  /**
   * Initialize carousel functionality
   */
  initCarousel() {
    if (!this.carousel || this.totalSlides <= 1) return;

    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.prevSlide();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nextSlide();
      });
    }

    // Hover to show second image (desktop only)
    if (!this.isMobile && this.totalSlides > 1) {
      const mediaElement = this.card.querySelector(".m-product-card__media");

      if (mediaElement) {
        mediaElement.addEventListener("mouseenter", () => {
          // Only auto-change if we're on the first slide
          if (this.currentSlide === 0) {
            this.hoverTimeout = setTimeout(() => {
              this.goToSlide(1);
              this.isHoverActive = true;
            }, 200); // Small delay to prevent accidental triggers
            this._pendingTimeouts.push(this.hoverTimeout);
          }
        });

        mediaElement.addEventListener("mouseleave", () => {
          // Clear timeout if mouse leaves before delay
          if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
          }

          // Return to first slide if hover was active and no manual navigation occurred
          if (this.isHoverActive && this.currentSlide === 1) {
            this.goToSlide(0);
          }
          this.isHoverActive = false;
        });
      }
    }

    // Touch/swipe support
    if (this.carouselTrack) {
      this.carouselTrack.addEventListener(
        "touchstart",
        (e) => {
          this.touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true },
      );

      this.carouselTrack.addEventListener(
        "touchend",
        (e) => {
          this.touchEndX = e.changedTouches[0].screenX;
          this.handleSwipe();
        },
        { passive: true },
      );
    }

    this.updateCarouselButtons();
  }

  /**
   * Navigate to previous slide
   */
  prevSlide() {
    // Disable hover auto-return when manually navigating
    this.isHoverActive = false;

    if (this.currentSlide > 0) {
      this.goToSlide(this.currentSlide - 1);
    } else {
      // Loop to last slide
      this.goToSlide(this.totalSlides - 1);
    }
  }

  /**
   * Navigate to next slide
   */
  nextSlide() {
    // Disable hover auto-return when manually navigating
    this.isHoverActive = false;

    if (this.currentSlide < this.totalSlides - 1) {
      this.goToSlide(this.currentSlide + 1);
    } else {
      // Loop to first slide
      this.goToSlide(0);
    }
  }

  /**
   * Go to specific slide
   */
  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;

    // Remove active class from current slide
    this.slides[this.currentSlide].classList.remove("is-active");

    // Add active class to new slide
    this.slides[index].classList.add("is-active", "is-transitioning");

    // Remove transitioning class after animation
    const timeoutId = setTimeout(() => {
      this.slides[index].classList.remove("is-transitioning");
    }, 300);
    this._pendingTimeouts.push(timeoutId);

    this.currentSlide = index;
    this.updateCarouselButtons();
  }

  /**
   * Update carousel button states
   */
  updateCarouselButtons() {
    // For looping carousel, buttons are always enabled
    // If you want to disable at ends, uncomment below:
    /*
    if (this.prevButton) {
      this.prevButton.disabled = this.currentSlide === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentSlide === this.totalSlides - 1;
    }
    */
  }

  /**
   * Handle touch swipe
   */
  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next slide
        this.nextSlide();
      } else {
        // Swipe right - prev slide
        this.prevSlide();
      }
    }
  }

  /**
   * Initialize quick-add functionality
   */
  initQuickAdd() {
    // Quick add trigger buttons (only for multi-variant products).
    // There can be more than one trigger per card (e.g. media-anchored hover
    // button + price-row inline mobile button); bind the same handler on each.
    if (this.quickAddTriggers && this.quickAddTriggers.length) {
      _scpDebugLog("product-card:trigger-bound", {
        productId: this.productId,
        count: this.quickAddTriggers.length,
        hasOverlay: !!this.variantOverlay,
        inFeatureCarousel: !!(
          this.card.closest && this.card.closest("scp-feature-carousel")
        ),
        inSwiperDuplicate: !!(
          this.card.closest && this.card.closest(".swiper-slide-duplicate")
        ),
      });
      this.quickAddTriggers.forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          _scpDebugLog("product-card:trigger-fired", {
            productId: this.productId,
            viaListener: "per-instance",
            overlayWasActive: !!(
              this.variantOverlay &&
              this.variantOverlay.classList.contains("is-active")
            ),
            overlayPresent: !!this.variantOverlay,
            triggerClasses:
              (trigger.className && String(trigger.className)) || "",
          });
          e.preventDefault();
          e.stopPropagation();

          // Show variant overlay for multi-variant products
          this.showVariantOverlay();
        });
      });
    }

    // Quick add single button (for products without size option)
    const quickAddSingleButton = this.card.querySelector(
      "[data-quick-add-single]",
    );
    if (quickAddSingleButton) {
      quickAddSingleButton.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const variantId = quickAddSingleButton.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId);
        }
      });
    }

    // Overlay close button
    if (this.overlayClose) {
      this.overlayClose.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hideVariantOverlay();
      });
    }

    // Size options (click to add to cart using theme's method)
    this.sizeOptions.forEach((option) => {
      option.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!option.disabled) {
          const variantId = option.dataset.variantId;
          if (variantId) {
            this.addToCart(variantId);
            this.hideVariantOverlay();
          }
        }
      });
    });

    // Listen for successful add-to-cart from theme's product-form
    document.addEventListener("product:added-to-cart", () => {
      this.hideVariantOverlay();
    });
  }

  /**
   * Show variant overlay
   */
  showVariantOverlay() {
    const overlayClassesBefore = this.variantOverlay
      ? this.variantOverlay.className
      : null;
    if (this.variantOverlay) {
      // Update size options based on currently selected color
      this.updateSizeOptionsForSelectedColor();

      this.variantOverlay.classList.add("is-active");
    }
    _scpDebugLog("product-card:show-overlay", {
      productId: this.productId,
      overlayFound: !!this.variantOverlay,
      overlayClassesBefore: overlayClassesBefore,
      overlayClassesAfter: this.variantOverlay
        ? this.variantOverlay.className
        : null,
    });
  }

  /**
   * Update size options based on the currently selected color
   */
  updateSizeOptionsForSelectedColor() {
    if (!this.productData || !this.productData.variants) return;

    // Get the currently selected color
    const selectedColorSwatch = this.card.querySelector(
      '.m-product-option--node__label[data-selected="true"]',
    );
    if (!selectedColorSwatch) {
      return;
    }

    const selectedColor = selectedColorSwatch.dataset.value;
    const colorPosition =
      parseInt(selectedColorSwatch.dataset.optionPosition) || 1;

    // Find the size option position
    let sizePosition = null;
    for (let i = 0; i < this.productData.options.length; i++) {
      const optionName = this.productData.options[i].toLowerCase();
      if (optionName.includes("size") || optionName.includes("tamanho")) {
        sizePosition = i + 1; // Shopify uses 1-based indexing
        break;
      }
    }

    if (!sizePosition) {
      return;
    }
    // Update each size option button with the correct variant ID
    const sizeOptions = this.card.querySelectorAll("[data-size-option]");
    sizeOptions.forEach((button) => {
      const sizeValue = button.dataset.sizeValue;

      // Find the matching variant for this color + size combination
      const matchingVariant = this.productData.variants.find((variant) => {
        const variantColor =
          colorPosition === 1
            ? variant.option1
            : colorPosition === 2
              ? variant.option2
              : variant.option3;

        const variantSize =
          sizePosition === 1
            ? variant.option1
            : sizePosition === 2
              ? variant.option2
              : variant.option3;

        return variantColor === selectedColor && variantSize === sizeValue;
      });
      if (matchingVariant) {
        // Update the variant ID
        button.dataset.variantId = matchingVariant.id;

        // Update availability
        if (matchingVariant.available) {
          button.classList.remove("scp-product-card__size-option--disabled");
          button.disabled = false;
        } else {
          button.classList.add("scp-product-card__size-option--disabled");
          button.disabled = true;
        }
      } else {
        // No matching variant found - disable the button
        button.classList.add("scp-product-card__size-option--disabled");
        button.disabled = true;
        button.dataset.variantId = "";
      }
    });
  }

  /**
   * Hide variant overlay
   */
  hideVariantOverlay() {
    if (this.variantOverlay) {
      this.variantOverlay.classList.remove("is-active");
    }
  }

  /**
   * Add product to cart (using native Minimog pattern with sections for drawer refresh)
   */
  async addToCart(variantId) {
    if (!variantId) return;

    this.card.classList.add("scp-product-card--loading");

    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }),
      );

      const cartAddUrl =
        window.MinimogSettings?.routes?.cart_add_url || "/cart/add.js";

      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);
      formData.append("sections", sectionsToBundle.join(","));
      formData.append("sections_url", window.location.pathname);

      const response = await fetch(cartAddUrl, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        body: formData,
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.error(
          "[SCP Product Card] Non-JSON response. Status:",
          response.status,
          "Content-Type:",
          contentType,
        );
        throw new Error(
          "Server returned an invalid response. Please refresh and try again.",
        );
      }

      const data = await response.json();

      if (data.status) {
        throw new Error(
          data.description ||
            data.message ||
            window.MinimogStrings?.addToCartError ||
            "Failed to add to cart",
        );
      }

      if (
        window.MinimogEvents &&
        window.MinimogTheme?.pubSubEvents?.cartUpdate
      ) {
        window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
          cart: data,
          productVariantId: variantId,
        });
      }

      const cartDrawer = document.querySelector("m-cart-drawer");
      if (cartDrawer) {
        if (
          window.MinimogTheme?.Notification &&
          window.MinimogStrings?.itemAdded
        ) {
          MinimogTheme.Notification.show({
            target: cartDrawer.querySelector("m-cart-items"),
            method: "prepend",
            type: "success",
            message: MinimogStrings.itemAdded,
            delay: 400,
          });
        }
        cartDrawer.open();
      }

      document.dispatchEvent(
        new CustomEvent("product-ajax:added", {
          detail: { product: data },
        }),
      );
    } catch (error) {
      console.error("[SCP Product Card] Add to cart error:", error);
      this.showErrorFeedback(
        error.message ||
          window.MinimogStrings?.addToCartError ||
          "Failed to add product to cart. Please try again.",
      );
    } finally {
      this.card.classList.remove("scp-product-card--loading");
    }
  }

  /**
   * Show error feedback (leverages theme's notification system)
   */
  showErrorFeedback(
    message = window.MinimogStrings?.addToCartError ||
      "Failed to add product to cart. Please try again.",
  ) {
    console.error("[SCP Product Card] Error feedback:", message);
    if (window.MinimogTheme && window.MinimogTheme.Notification) {
      window.MinimogTheme.Notification.show({
        target: document.body,
        type: "warning",
        message: message,
        delay: 5000,
        sticky: true,
      });
    }
  }

  /**
   * Initialize mobile-specific behavior
   */
  initMobileBehavior() {
    if (!this.isMobile) return;

    // On mobile, clicking the card should go to product page
    // The overlay is hidden via CSS
    // Variant swatches are handled by theme's product-card-swatch.js
  }

  /**
   * Initialize color swatch functionality
   * Listen for color changes and update price
   */
  initColorSwatches() {
    // Listen for color swatch changes from the theme's product-card-swatch component
    const swatchContainer = this.card.querySelector(
      "[data-pcard-variant-picker]",
    );

    if (!swatchContainer) return;

    // Listen for click events on color swatches
    const colorSwatches = this.card.querySelectorAll(
      ".m-product-option--node__label",
    );

    colorSwatches.forEach((swatch) => {
      swatch.addEventListener("click", () => {
        // Small delay to ensure the theme has updated the variant
        const timeoutId = setTimeout(() => {
          this.updatePriceFromSwatchChange();
        }, 50);
        this._pendingTimeouts.push(timeoutId);
      });
    });

    // Also listen for the custom event that the theme might dispatch
    this.card.addEventListener("variant:change", () => {
      this.updatePriceFromSwatchChange();
    });
  }

  /**
   * Update price when color swatch changes
   */
  updatePriceFromSwatchChange() {
    if (!this.productData || !this.productData.variants) return;

    // Get the currently selected color
    const selectedColorSwatch = this.card.querySelector(
      '.m-product-option--node__label[data-selected="true"]',
    );

    if (!selectedColorSwatch) return;

    const selectedColor = selectedColorSwatch.dataset.value;
    const colorPosition =
      parseInt(selectedColorSwatch.dataset.optionPosition) || 1;

    // Find the first available variant with this color
    const matchingVariant =
      this.productData.variants.find((variant) => {
        const variantColor =
          colorPosition === 1
            ? variant.option1
            : colorPosition === 2
              ? variant.option2
              : variant.option3;

        return variantColor === selectedColor && variant.available;
      }) ||
      this.productData.variants.find((variant) => {
        // If no available variant, find any variant with this color
        const variantColor =
          colorPosition === 1
            ? variant.option1
            : colorPosition === 2
              ? variant.option2
              : variant.option3;

        return variantColor === selectedColor;
      });

    if (matchingVariant) {
      this.updatePrice(matchingVariant);
    }
  }

  /**
   * Update price display for a variant
   */
  updatePrice(variant) {
    if (!this.priceWrapper) return;

    const priceElement = this.priceWrapper.querySelector(
      ".m-price-item--regular, .m-price",
    );
    const salePriceElement = this.priceWrapper.querySelector(
      ".m-price-item--sale",
    );
    const compareElement = this.priceWrapper.querySelector(
      ".m-price-item--regular s, .m-price__sale s",
    );

    // SCP custom pricing elements
    const scpSecondaryContainer = this.priceWrapper.querySelector(
      ".m-price__scp-secondary",
    );
    const scpMemberPriceElement = this.priceWrapper.querySelector(
      ".m-price-item--member",
    );

    // Format price using theme's global formatter with fallback
    const formatPrice = (cents) => {
      if (
        typeof formatMoney === "function" &&
        window.MinimogSettings?.money_format
      ) {
        return formatMoney(cents, window.MinimogSettings.money_format);
      }
      const amount = cents / 100;
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: window.Shopify?.currency?.active || "EUR",
      }).format(amount);
    };

    // Update regular price
    if (priceElement) {
      priceElement.textContent = formatPrice(variant.price);
    }

    // Update sale price if exists
    if (salePriceElement) {
      salePriceElement.textContent = formatPrice(variant.price);
    }

    // Update compare at price if exists
    if (compareElement && variant.compare_at_price > variant.price) {
      compareElement.textContent = formatPrice(variant.compare_at_price);
      compareElement.style.display = "";
    } else if (compareElement) {
      compareElement.style.display = "none";
    }

    // Update SCP member price if exists
    // Note: SCP member price uses giaf.member_price metafield which is stored in actual currency (not cents)
    // The Liquid template handles the conversion, but for client-side updates we check variant metafields
    if (scpMemberPriceElement && variant.metafields?.giaf?.member_price) {
      const memberPriceRaw = parseFloat(variant.metafields.giaf.member_price);
      if (memberPriceRaw > 0) {
        // Convert to cents for formatPrice (member_price is stored as actual currency value)
        const memberPriceCents = Math.round(memberPriceRaw * 100);
        scpMemberPriceElement.textContent = formatPrice(memberPriceCents);
        if (scpSecondaryContainer) {
          scpSecondaryContainer.style.display = "";
        }
      }
    }

    // Update sale/regular price visibility based on whether there's a discount
    const priceContainer = this.priceWrapper.querySelector(".m-price");
    if (priceContainer) {
      if (variant.compare_at_price > variant.price) {
        priceContainer.classList.add("m-price--on-sale");
      } else {
        priceContainer.classList.remove("m-price--on-sale");
      }
    }
  }

  /**
   * Safely parse product JSON data with error handling
   */
  getProductData() {
    const productJsonElement = this.card.querySelector("[data-product-json]");
    if (!productJsonElement) return null;

    try {
      return JSON.parse(productJsonElement.textContent);
    } catch (e) {
      console.error("[SCP Product Card] Failed to parse product JSON:", e);
      return null;
    }
  }

  /**
   * Clean up pending timeouts and event listeners
   */
  cleanup() {
    // Clear all pending timeouts
    if (this._pendingTimeouts && this._pendingTimeouts.length > 0) {
      this._pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      this._pendingTimeouts = [];
    }

    // Clear hover timeout specifically
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  /**
   * Debounce utility
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

/**
 * Initialize all SCP Product Cards
 */
function initSCPProductCards() {
  const cards = document.querySelectorAll("[data-scp-product-card]");
  cards.forEach((card) => {
    // Skip Swiper clones (e.g. scp-feature-carousel loop duplicates) so that
    // hover/touch listeners are only attached to originals. The feature
    // carousel handles clone-specific wiring itself via initProductCards().
    if (card.closest(".swiper-slide-duplicate")) return;
    if (!card.scpProductCard) {
      card.scpProductCard = new SCPProductCard(card);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSCPProductCards);
} else {
  initSCPProductCards();
}

// Re-initialize on dynamic content load (e.g., AJAX pagination)
document.addEventListener("shopify:section:load", initSCPProductCards);

// Expose so AJAX-injected card containers (e.g. wishlist, product-list) can re-init.
window.initSCPProductCards = initSCPProductCards;

// Cleanup on section unload to prevent memory leaks
document.addEventListener("shopify:section:unload", (event) => {
  const section = event.target;
  const cards = section.querySelectorAll("[data-scp-product-card]");
  cards.forEach((card) => {
    if (
      card.scpProductCard &&
      typeof card.scpProductCard.cleanup === "function"
    ) {
      card.scpProductCard.cleanup();
      delete card.scpProductCard;
    }
  });
});

// Export for use in other scripts
window.SCPProductCard = SCPProductCard;
window.initSCPProductCards = initSCPProductCards;
